// src/services/shopifyService.js

// To fetch products for the "MyBeeHome" sales channel, we will use a dedicated Shopify Collection.
// Please create a collection in your Shopify admin (e.g., named "MyBeeHome Products")
// and add all relevant products to this collection.
// Then, replace the placeholder handle or GID below with your collection's handle or GID.

// Option 1: Using Collection Handle (often easier to find and manage)
// Example: "mybeehome-products" (lowercase, spaces replaced by hyphens)
const MYBEEHOME_COLLECTION_HANDLE = "mybeehome-products"; // <-- IMPORTANT: REPLACE WITH YOUR ACTUAL COLLECTION HANDLE

// Option 2: Using Collection GID (Global ID) - Use this if you prefer or if handles are not unique enough.
// Example: "gid://shopify/Collection/1234567890"
// const MYBEEHOME_COLLECTION_GID = "gid://shopify/Collection/1234567890"; // <-- IMPORTANT: REPLACE WITH YOUR ACTUAL COLLECTION GID

/**
 * Fetches products specifically for the "MyBeeHome" sales channel.
 * This is achieved by fetching all products from a dedicated Shopify collection
 * that is curated for the MyBeeHome channel.
 *
 * @param {string} language - The language for the Shopify client (e.g., 'de-CH')
 * @returns {Promise<Array>} A promise that resolves to an array of product variants.
 */
export const fetchBeeHomeProducts = async (language = "de-CH") => {
  let shopifyClient;

  try {
    // Dynamic import to avoid bundling issues
    const Client = (await import("shopify-buy")).default;

    // Build Shopify client locally
    shopifyClient = Client.buildClient({
      domain: new URL(JSON.parse(process.env.REACT_APP_SHOPIFY_URL)[language])
        .host,
      storefrontAccessToken: JSON.parse(
        process.env.REACT_APP_SHOPIFY_ACCESSTOKEN
      )[language],
      language: language.split("-")[1] ?? "DE",
    });
  } catch (error) {
    console.error("Error initializing Shopify client:", error);
    return [];
  }

  try {
    let collection;
    let productsFromCollection = [];

    // --- Fetching by Collection Handle (Recommended Approach) ---
    // Ensure the handle has been updated from the placeholder
    if (
      MYBEEHOME_COLLECTION_HANDLE &&
      MYBEEHOME_COLLECTION_HANDLE !== "mybeehome-products"
    ) {
      console.log(
        `Attempting to fetch collection by handle: ${MYBEEHOME_COLLECTION_HANDLE}`
      );
      collection = await shopifyClient.collection.fetchByHandle(
        MYBEEHOME_COLLECTION_HANDLE
      );
      if (collection) {
        console.log(
          `Successfully fetched collection: ${collection.title}. Now fetching products.`
        );
        const collectionWithProducts =
          await shopifyClient.collection.fetchWithProducts(collection.id, {
            productsFirst: 150,
          });
        productsFromCollection = collectionWithProducts.products || [];
      } else {
        console.error(
          `Collection with handle "${MYBEEHOME_COLLECTION_HANDLE}" not found.`
        );
      }
    }
    // --- Fallback or alternative: Fetching by Collection GID ---
    // Uncomment and use this section if you prefer to use the GID directly.
    // Ensure the GID has been updated from the placeholder.
    // else if (MYBEEHOME_COLLECTION_GID && MYBEEHOME_COLLECTION_GID !== "gid://shopify/Collection/1234567890") {
    //   console.log(`Attempting to fetch collection by GID: ${MYBEEHOME_COLLECTION_GID}`);
    //   // fetchWithProducts can directly take a GID string
    //   const collectionWithProducts = await shopifyClient.collection.fetchWithProducts(MYBEEHOME_COLLECTION_GID, { productsFirst: 150 });
    //   productsFromCollection = collectionWithProducts.products || [];
    //   if (productsFromCollection.length > 0) {
    //     console.log(`Successfully fetched products using GID. First product title: ${productsFromCollection[0].title}`);
    //   } else {
    //     console.error(`No products found for collection GID: ${MYBEEHOME_COLLECTION_GID}`);
    //   }
    // }
    else {
      // This block will run if the handle/GID is still the placeholder.
      console.error(
        "CRITICAL: Please configure MYBEEHOME_COLLECTION_HANDLE (or MYBEEHOME_COLLECTION_GID) in shopifyService.js with your MyBeeHome collection's identifier. Using placeholder value."
      );
      // As a fallback to prevent breaking the app entirely if not configured, you might return an empty array
      // or, if you want to test with a known product type, you could fall back to the old logic here.
      // For now, we'll return an empty array to enforce configuration.
      return [];
    }

    console.log(
      `Fetched ${productsFromCollection.length} products from the MyBeeHome collection.`
    );

    const transformedVariants = productsFromCollection.flatMap((product) => {
      // Ensure product.variants exists and is an array before mapping
      if (!product.variants || !Array.isArray(product.variants)) {
        console.warn(
          `Product ${product.title} in MyBeeHome collection has no variants or variants is not an array.`
        );
        return [];
      }

      return product.variants
        .map((variant) => {
          // You might still want to filter by SKU if not all products in the collection are desired
          // if (FILTERED_SKUS.length > 0 && !FILTERED_SKUS.includes(variant.sku)) {
          //   return null; // Skip this variant if its SKU is not in the list
          // }

          console.log(
            `Processing variant from MyBeeHome collection: ${variant.sku} for product: ${product.title}`
          );
          return {
            id: variant.id,
            sku: variant.sku,
            title: variant.title, // Variant title (e.g., "Default Title", "Large")
            price: variant.price.amount,
            currency: variant.price.currencyCode,
            available: variant.available,
            productTitle: product.title, // Main product title
            description: product.descriptionHtml || product.description,
            image:
              variant.image?.src ||
              product.images[0]?.src ||
              "/images/placeholder.png", // Ensure you have a placeholder image
            // Add any other fields you need from the product or variant
            productId: product.id, // Useful for linking back to the full product
          };
        })
        .filter(Boolean); // Filter out any null values if SKU filtering was applied
    });

    console.log(
      "Transformed variants from MyBeeHome collection:",
      transformedVariants
    );
    return transformedVariants;
  } catch (error) {
    console.error(
      `Error fetching Shopify products for MyBeeHome collection:`,
      error
    );
    return []; // Return an empty array on error
  }
};

/**
 * Fetches products tagged with "beehome_addon" from Shopify using the REST API.
 * It uses a query parameter to filter products by tag directly on the server.
 *
 * @param {string} language - The language for the Shopify client (e.g., 'de-CH')
 * @returns {Promise<Array>} A promise that resolves to an array of product variants.
 */
export const fetchBeeHomeAddOnProducts = async (language = "de-CH") => {
  let shopifyClient;
  const ADD_ON_TAG = "beehome_addon";

  try {
    // Dynamic import to avoid bundling issues, similar to other functions
    const Client = (await import("shopify-buy")).default;
    if (!Client) {
      console.error("Shopify Buy Client could not be imported.");
      return [];
    }

    const shopifyUrl = JSON.parse(process.env.REACT_APP_SHOPIFY_URL);
    const shopifyAccessToken = JSON.parse(
      process.env.REACT_APP_SHOPIFY_ACCESSTOKEN
    );

    if (!shopifyUrl || !shopifyUrl[language]) {
      console.error(
        `Shopify URL not configured for language: ${language}`,
        process.env.REACT_APP_SHOPIFY_URL
      );
      return [];
    }
    if (!shopifyAccessToken || !shopifyAccessToken[language]) {
      console.error(
        `Shopify Access Token not configured for language: ${language}`,
        process.env.REACT_APP_SHOPIFY_ACCESSTOKEN
      );
      return [];
    }

    shopifyClient = Client.buildClient({
      domain: new URL(shopifyUrl[language]).host,
      storefrontAccessToken: shopifyAccessToken[language],
      language: language,
    });

    if (!shopifyClient) {
      console.error("Shopify client was not built successfully.");
      return [];
    }
    console.log(
      "Shopify client initialized successfully for fetching add-ons.",
      language
    );
  } catch (error) {
    console.error("Error initializing Shopify client for add-ons:", error);
    if (error instanceof SyntaxError) {
      console.error(
        "SyntaxError parsing REACT_APP_SHOPIFY_URL or REACT_APP_SHOPIFY_ACCESSTOKEN. Check if they are valid JSON strings."
      );
    }
    return [];
  }

  try {
    console.log(
      `Fetching products with tag: "${ADD_ON_TAG}" using query parameter.`
    );
    const fetchedProducts = await shopifyClient.product.fetchQuery({
      first: 50, // Should be enough for add-ons
      query: `tag:'${ADD_ON_TAG}'`,
    });

    if (!fetchedProducts || fetchedProducts.length === 0) {
      console.log(`No products found with tag "${ADD_ON_TAG}".`);
      return [];
    }

    console.log(
      `Found ${fetchedProducts.length} products with tag "${ADD_ON_TAG}".`
    );

    const transformedVariants = fetchedProducts.flatMap((product) => {
      if (!product.variants || !Array.isArray(product.variants)) {
        console.warn(
          `Product ${product.title} with tag ${ADD_ON_TAG} has no variants.`
        );
        return [];
      }
      return product.variants
        .filter((variant) => variant.available) // Only include available variants
        .map((variant) => {
          return {
            id: variant.id,
            sku: variant.sku,
            title: variant.title,
            price: variant.price.amount,
            currency: variant.price.currencyCode,
            available: variant.available,
            productTitle: product.title,
            description: product.descriptionHtml || product.description,
            image:
              variant.image?.src ||
              product.images[0]?.src ||
              "/images/placeholder.png",
            productId: product.id,
          };
        });
    });

    transformedVariants.sort((a, b) => Number(b.price) - Number(a.price));

    console.log("Transformed add-on variants:", transformedVariants);
    return transformedVariants;
  } catch (error) {
    console.error(`Error fetching Shopify add-on products using query:`, error);
    return [];
  }
};

/**
 * Creates a Shopify order with additional options and tags
 * @param {Object} orderData - The order data including additionalOptions
 * @param {string} language - The language for the Shopify client
 * @returns {Promise<Object>} The created order response
 */
export const createShopifyOrderWithAdditionalOptions = async (
  orderData,
  language = "de-CH"
) => {
  try {
    const shopifyUrl = JSON.parse(process.env.REACT_APP_SHOPIFY_URL)[language];
    const accessToken = JSON.parse(process.env.REACT_APP_SHOPIFY_ACCESSTOKEN)[
      language
    ];

    // Create the order payload
    const orderPayload = {
      order: {
        line_items: [],
        tags: [],
        ...orderData.baseOrder,
      },
    };

    // Add main line items
    if (orderData.lineItems) {
      orderPayload.order.line_items.push(...orderData.lineItems);
    }

    // Process additionalOptions
    if (
      orderData.additionalOptions &&
      Array.isArray(orderData.additionalOptions)
    ) {
      orderData.additionalOptions.forEach((option) => {
        if (option.addOnDetails && Array.isArray(option.addOnDetails)) {
          option.addOnDetails.forEach((addOn) => {
            // Add as line item
            orderPayload.order.line_items.push({
              variant_id: addOn.id,
              quantity: 1,
              properties: [
                {
                  name: "house_code",
                  value: option.houseCode || "",
                },
              ],
            });

            // Add tag with format: <optionsku>_<housecode>
            if (addOn.sku && option.houseCode) {
              const tag = `${addOn.sku}_${option.houseCode}`;
              orderPayload.order.tags.push(tag);
            }
          });
        }
      });
    }

    // Convert tags array to comma-separated string as required by Shopify
    orderPayload.order.tags = orderPayload.order.tags.join(", ");

    console.log("Creating Shopify order with payload:", orderPayload);

    // Create order via REST Admin API
    const response = await fetch(
      `${shopifyUrl}/admin/api/2023-10/orders.json`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Access-Token": accessToken,
        },
        body: JSON.stringify(orderPayload),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        `Shopify order creation failed: ${JSON.stringify(errorData)}`
      );
    }

    const result = await response.json();
    console.log("Shopify order created successfully:", result);
    return result;
  } catch (error) {
    console.error(
      "Error creating Shopify order with additional options:",
      error
    );
    throw error;
  }
};

// If you had a sortVariantsBySku function, you could include it here or import it
// const sortVariantsBySku = (variants) => {
//   return variants.sort((a, b) => a.sku.localeCompare(b.sku));
// };
